import React, { useState, useEffect, useCallback, memo } from "react";
import { Table, Select, DatePicker, Space, Typography, Button, Modal, Form, Input, Spin, message } from "antd";
import { ExportOutlined, PlusOutlined } from "@ant-design/icons";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import moment from "moment";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { resolvePreSignedUrl } from "../../../utils/MyMUtils";
import { getAllSessions, addManualSession, getStaffListing, getActiveFacilities } from "./workpulseApi";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const staffDisplayName = (item) => {
  const first = item?.first_name || item?.firstName || "";
  const last = item?.last_name || item?.lastName || "";
  return `${first} ${last}`.trim() || item?.full_name || item?.name || "Unknown";
};

const formatHours = (minutes) => (typeof minutes === "number" ? (minutes / 60).toFixed(2) : "");

// No check-out yet: still open if the shift started today (they may still check out later
// today), but if it started on an earlier day, it should have closed out already — call it
// missed rather than implying it's still in progress.
const clockOutLabel = (r) => {
  if (r.check_out_datetime) return moment(r.check_out_datetime).format("HH:mm");
  return moment(r.check_in_datetime).isSame(moment(), "day") ? "Still open" : "Missed";
};

const writeExcel = (rows, fileName) => {
  if (!rows.length) { message.error("There is no data to export."); return; }
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([buffer], { type: FILE_TYPE }), `${fileName}.xlsx`);
};

// ---- Backfill a session the kiosk never recorded (e.g. staff forgot to use the tablet) ----
const ManualEntryModal = ({ visible, staff, facilities, onClose, onAdded }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) form.resetFields();
  }, [visible, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { error, result } = await addManualSession({
        user_id: values.user_id,
        facility_id: values.facility_id,
        check_in_datetime: values.check_in.format("YYYY-MM-DD HH:mm:ss"),
        check_out_datetime: values.check_out ? values.check_out.format("YYYY-MM-DD HH:mm:ss") : undefined,
        reason: values.reason,
      });
      if (error) message.error(result?.message || "Failed to add entry.");
      else { message.success("Entry added."); onAdded(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Manual Entry" visible={visible} onCancel={onClose} onOk={handleSave} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="user_id" label="Staff Member" rules={[{ required: true, message: "Please select a staff member." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select staff">
            {staff.map((s) => <Option key={s.id ?? s.user_id} value={s.id ?? s.user_id}>{staffDisplayName(s)}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="facility_id" label="Facility" rules={[{ required: true, message: "Please select a facility." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select facility">
            {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="check_in" label="Clock In" rules={[{ required: true, message: "Please enter the clock-in date/time." }]}>
          <DatePicker showTime format={`${DATE_FORMAT_DD_MM_YYYY} HH:mm`} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="check_out"
          label="Clock Out (optional)"
          dependencies={["check_in"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || !getFieldValue("check_in") || value.isAfter(getFieldValue("check_in"))) return Promise.resolve();
                return Promise.reject(new Error("Clock out must be after clock in"));
              },
            }),
          ]}
        >
          <DatePicker showTime format={`${DATE_FORMAT_DD_MM_YYYY} HH:mm`} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: "Please enter a reason." }]}>
          <Input.TextArea rows={2} placeholder="Why is this being added manually?" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// check_in_photo_url points at a private S3 object — it 403s if used directly as an <img src>,
// so each thumbnail resolves its own short-lived signed URL before rendering. Uses a plain
// <img> + its own Modal for the full-size view rather than antd's Image preview mask, which
// doesn't fit cleanly inside a 36px table thumbnail.
const ClockInPhoto = ({ url }) => {
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolvedSrc(null);
    setFailed(false);
    resolvePreSignedUrl(url).then((signedUrl) => {
      if (cancelled) return;
      if (signedUrl) setResolvedSrc(signedUrl);
      else setFailed(true);
    });
    return () => { cancelled = true; };
  }, [url]);

  if (failed) return <Text type="secondary">-</Text>;
  if (!resolvedSrc) return <Spin size="small" />;
  return (
    <>
      <img
        src={resolvedSrc}
        alt="Clock-in"
        width={36}
        height={36}
        style={{ objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
        onClick={() => setPreviewOpen(true)}
      />
      <Modal visible={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} title="Clock-in Photo" destroyOnClose>
        <img src={resolvedSrc} alt="Clock-in full size" style={{ width: "100%", display: "block" }} />
      </Modal>
    </>
  );
};

const Timesheet = () => {
  const [staff, setStaff] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterUserId, setFilterUserId] = useState(null);
  const [filterFacilityId, setFilterFacilityId] = useState(null);
  const [dateRange, setDateRange] = useState([moment().startOf("month"), moment().endOf("month")]);
  const [manualEntryVisible, setManualEntryVisible] = useState(false);

  const loadStaff = useCallback(async () => {
    const params = new URLSearchParams({ page: "1", limit: "200" });
    const { error, result } = await getStaffListing(params);
    if (error) { setStaff([]); return; }
    const payload = result?.result ?? result ?? {};
    setStaff(Array.isArray(payload?.data) ? payload.data : []);
  }, []);

  const loadFacilities = useCallback(async () => {
    const { error, result } = await getActiveFacilities();
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "1000" });
    if (dateRange?.[0]) params.set("from", dateRange[0].format("YYYY-MM-DD 00:00:00"));
    if (dateRange?.[1]) params.set("to", dateRange[1].format("YYYY-MM-DD 23:59:59"));
    if (filterUserId) params.set("user_id", filterUserId);
    if (filterFacilityId) params.set("facility_id", filterFacilityId);
    const { error, result } = await getAllSessions(params);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [dateRange, filterUserId, filterFacilityId]);

  useEffect(() => { loadStaff(); loadFacilities(); }, [loadStaff, loadFacilities]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleExport = useCallback(() => {
    const exportRows = rows.map((r) => ({
      Facility: r.facility_name || "",
      Employee: staffDisplayName(r),
      Day: moment(r.check_in_datetime).format("ddd"),
      Date: moment(r.check_in_datetime).format(DATE_FORMAT_DD_MM_YYYY),
      "Clock IN": moment(r.check_in_datetime).format("HH:mm"),
      "Clock OUT": clockOutLabel(r),
      "Clock IN Photo": r.check_in_photo_url || "",
      "Rostered Hours": formatHours(r.scheduled_minutes) || "00",
      "Worked Hours": formatHours(r.duration_minutes),
    }));
    const rangeLabel = dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].format("YYYY-MM-DD")}_to_${dateRange[1].format("YYYY-MM-DD")}`
      : moment().format("YYYY-MM-DD");
    writeExcel(exportRows, `workpulse_timesheet_${rangeLabel}`);
  }, [rows, dateRange]);

  const columns = [
    { title: "Employee", key: "employee", render: (_, r) => staffDisplayName(r) },
    { title: "Day", key: "day", render: (_, r) => moment(r.check_in_datetime).format("ddd") },
    { title: "Date", key: "date", render: (_, r) => moment(r.check_in_datetime).format(DATE_FORMAT_DD_MM_YYYY) },
    { title: "Facility", dataIndex: "facility_name", key: "facility_name" },
    { title: "Clock IN", key: "clock_in", render: (_, r) => moment(r.check_in_datetime).format("HH:mm") },
    {
      title: "Clock OUT",
      key: "clock_out",
      render: (_, r) => (r.check_out_datetime ? moment(r.check_out_datetime).format("HH:mm") : <Text type="secondary">{clockOutLabel(r)}</Text>),
    },
    {
      // Only clock-in ever captures a photo — check-out never has, by design (no photo
      // required to end a shift), so there's nothing to show in that direction.
      title: "Clock IN Photo",
      key: "photo",
      render: (_, r) => (r.check_in_photo_url
        ? <ClockInPhoto url={r.check_in_photo_url} />
        : <Text type="secondary">-</Text>),
    },
    {
      title: "Rostered Hours",
      key: "scheduled_hours",
      render: (_, r) => formatHours(r.scheduled_minutes) || "00",
    },
    {
      title: "Worked Hours",
      key: "actual_hours",
      render: (_, r) => formatHours(r.duration_minutes) || "-",
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <Space size="large">
          <Select allowClear showSearch optionFilterProp="children" placeholder="Filter by staff member" style={{ width: 220 }} value={filterUserId} onChange={setFilterUserId}>
            {staff.map((s) => <Option key={s.id ?? s.user_id} value={s.id ?? s.user_id}>{staffDisplayName(s)}</Option>)}
          </Select>
          <Select allowClear showSearch optionFilterProp="children" placeholder="Filter by facility" style={{ width: 220 }} value={filterFacilityId} onChange={setFilterFacilityId}>
            {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
          </Select>
          <RangePicker value={dateRange} onChange={setDateRange} allowClear={false} format={DATE_FORMAT_DD_MM_YYYY} />
        </Space>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => setManualEntryVisible(true)}>Add Entry</Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>Export</Button>
        </Space>
      </div>
      <Table
        rowKey="session_id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No sessions in this range." }}
      />
      <ManualEntryModal
        visible={manualEntryVisible}
        staff={staff}
        facilities={facilities}
        onClose={() => setManualEntryVisible(false)}
        onAdded={loadSessions}
      />
    </div>
  );
};

export default memo(Timesheet);
